// Bar chart: bars ≤ 24px thick, 4px rounded data-end and square at the baseline, 2px surface gap
// between adjacent bars, value labels at the tip, per-bar hover/focus tooltip, legend for 2+ series.
import { useState } from 'react';
import { formatValue, niceTicks, seriesColor } from '../../lib/format';
import { useSize } from '../../lib/useSize';
import type { RegistryEntry, WidgetComponentProps } from '../types';

interface Model {
  categories: { key: string; label: string; marker?: number | null }[];
  series: { id: string; label: string; format: string; values: (number | null)[] }[];
  currency: string | null;
  markerLabel?: string;
}

export function toModel(data: any, props: any): Model {
  if (props.valueKey) {
    const rows: any[] = data.rows ?? [];
    return {
      categories: rows.map((r, i) => ({ key: String(r.sku ?? r.key ?? i), label: String(r[props.labelKey ?? 'label'] ?? ''), marker: props.markerKey ? r[props.markerKey] : undefined })),
      series: [{ id: props.valueKey, label: props.valueLabel ?? 'Value', format: props.format ?? 'number', values: rows.map((r) => r[props.valueKey]) }],
      currency: data.currency ?? null,
      markerLabel: props.markerLabel,
    };
  }
  const cols: any[] = data.columns ?? [];
  const rows: any[] = data.rows ?? [];
  const use = props.grouped ? cols : cols.slice(0, 1);
  return {
    categories: rows.map((r) => ({ key: r.key, label: r.label })),
    series: use.map((c) => ({ id: c.id, label: c.label, format: c.format, values: rows.map((r) => r[c.id] ?? null) })),
    currency: data.currency ?? null,
  };
}

/** Path for a bar with a 4px rounded data end and a square baseline end. */
function barPath(x: number, y: number, w: number, h: number, dir: 'up' | 'right') {
  const r = Math.min(4, dir === 'up' ? w / 2 : h / 2, dir === 'up' ? h : w);
  if (dir === 'up') return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  return `M${x},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h - r}Q${x + w},${y + h} ${x + w - r},${y + h}H${x}Z`;
}

function BarChart({ data, props }: WidgetComponentProps) {
  const [ref, { width, height }] = useSize<HTMLDivElement>();
  const [tip, setTip] = useState<{ x: number; y: number; c: number; s: number } | null>(null);
  const model = toModel(data, props);
  const horizontal = props.orientation === 'horizontal';
  const fmt = (v: number | null, s: number, compact = false) => formatValue(v, model.series[s]?.format, { currency: model.currency, compact });
  const legendH = model.series.length > 1 || model.markerLabel ? 22 : 0;
  const values = model.series.flatMap((s) => s.values).filter((v): v is number => v !== null);
  const markers = model.categories.map((c) => c.marker).filter((v): v is number => typeof v === 'number');
  const ticks = niceTicks(0, Math.max(1e-9, ...values, ...markers) * 1.08, 4);
  const vMax = ticks[ticks.length - 1];
  const H = Math.max(0, height - legendH);

  let body: JSX.Element | null = null;
  if (width > 0 && H > 0) {
    if (horizontal) {
      const labelW = Math.min(170, Math.max(...model.categories.map((c) => c.label.length), 4) * 6.4 + 8);
      const m = { top: 4, right: 70, bottom: 4, left: labelW };
      const w = Math.max(0, width - m.left - m.right);
      const band = (H - m.top - m.bottom) / Math.max(1, model.categories.length);
      const thick = Math.min(24, band * 0.7);
      const xs = (v: number) => (v / vMax) * w;
      body = (
        <svg width={width} height={H} role="img" aria-label={model.categories.map((c, i) => `${c.label}: ${fmt(model.series[0].values[i], 0)}`).join('; ')}>
          <line x1={m.left} x2={m.left} y1={m.top} y2={H - m.bottom} stroke="var(--axis)" />
          {model.categories.map((c, i) => {
            const v = model.series[0].values[i];
            const cy = m.top + band * i + band / 2;
            const bw = v === null ? 0 : Math.max(1, xs(v));
            return (
              <g key={c.key}>
                <text x={m.left - 8} y={cy} dy="0.32em" textAnchor="end">
                  {c.label.length > 26 ? `${c.label.slice(0, 25)}…` : c.label}
                </text>
                {v !== null && (
                  <path
                    className="bar-grow-x"
                    style={{ ['--bi' as any]: i }}
                    d={barPath(m.left, cy - thick / 2, bw, thick, 'right')}
                    fill={seriesColor(model.series[0].id, 0)}
                    tabIndex={0}
                    onPointerEnter={() => setTip({ x: m.left + bw, y: cy, c: i, s: 0 })}
                    onPointerLeave={() => setTip(null)}
                    onFocus={() => setTip({ x: m.left + bw, y: cy, c: i, s: 0 })}
                    onBlur={() => setTip(null)}
                    aria-label={`${c.label}: ${fmt(v, 0)}`}
                  />
                )}
                {typeof c.marker === 'number' && (
                  <line x1={m.left + xs(c.marker)} x2={m.left + xs(c.marker)} y1={cy - thick / 2 - 3} y2={cy + thick / 2 + 3} stroke="var(--text-primary)" strokeWidth={2} />
                )}
                <text x={m.left + Math.max(bw, typeof c.marker === 'number' ? xs(c.marker) : 0) + 6} y={cy} dy="0.32em" className="tabular" style={{ fill: 'var(--text-secondary)' }}>
                  {v === null ? 'No data' : fmt(v, 0, true)}
                </text>
                <rect x={0} y={cy - band / 2} width={width} height={band} fill="transparent" onPointerEnter={() => setTip({ x: m.left + bw, y: cy, c: i, s: 0 })} onPointerLeave={() => setTip(null)} />
              </g>
            );
          })}
        </svg>
      );
    } else {
      const labelW = Math.max(...ticks.map((t) => fmt(t, 0, true).length)) * 6.6 + 10;
      const m = { top: 16, right: 8, bottom: 22, left: labelW };
      const w = Math.max(0, width - m.left - m.right);
      const h = Math.max(0, H - m.top - m.bottom);
      const groupW = w / Math.max(1, model.categories.length);
      const ns = model.series.length;
      const barW = Math.min(24, (groupW * 0.7 - (ns - 1) * 2) / ns);
      const ys = (v: number) => m.top + h - (v / vMax) * h;
      // Narrow charts (phones, 24 hours) label every n-th category and drop per-bar values that
      // wouldn't fit; the tooltip and table view still give every value.
      const labelEvery = Math.max(1, Math.ceil((Math.max(...model.categories.map((c) => c.label.length), 1) * 6.4 + 6) / groupW));
      const valueW = Math.max(...model.series.flatMap((s, si) => s.values.map((v) => fmt(v, si, true).length)), 1) * 6 + 4;
      const showValues = ns <= 2 && valueW <= groupW;
      body = (
        <svg width={width} height={H} role="img" aria-label={model.categories.map((c, i) => `${c.label}: ${model.series.map((s) => `${s.label} ${fmt(s.values[i], 0)}`).join(', ')}`).join('; ')}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={m.left} x2={m.left + w} y1={ys(t)} y2={ys(t)} stroke={t === 0 ? 'var(--axis)' : 'var(--grid)'} />
              <text x={m.left - 8} y={ys(t)} dy="0.32em" textAnchor="end" className="tabular">
                {fmt(t, 0, true)}
              </text>
            </g>
          ))}
          {model.categories.map((c, ci) => {
            const gx = m.left + groupW * ci + (groupW - (ns * barW + (ns - 1) * 2)) / 2;
            return (
              <g key={c.key}>
                {model.series.map((s, si) => {
                  const v = s.values[ci];
                  if (v === null) return null;
                  const bx = gx + si * (barW + 2);
                  const by = ys(Math.max(0, v));
                  return (
                    <g key={s.id}>
                      <path
                        className="bar-grow-y"
                        style={{ ['--bi' as any]: ci }}
                        d={barPath(bx, by, barW, Math.max(1, ys(0) - by), 'up')}
                        fill={seriesColor(s.id, si)}
                        tabIndex={0}
                        aria-label={`${c.label} — ${s.label}: ${fmt(v, si)}`}
                        onPointerEnter={() => setTip({ x: bx + barW / 2, y: by, c: ci, s: si })}
                        onPointerLeave={() => setTip(null)}
                        onFocus={() => setTip({ x: bx + barW / 2, y: by, c: ci, s: si })}
                        onBlur={() => setTip(null)}
                      />
                      {showValues && (
                        <text x={bx + barW / 2} y={by - 4} textAnchor="middle" className="tabular" style={{ fill: 'var(--text-secondary)', fontSize: 10.5 }}>
                          {fmt(v, si, true)}
                        </text>
                      )}
                    </g>
                  );
                })}
                {typeof c.marker === 'number' && (
                  <line
                    x1={gx - 3}
                    x2={gx + ns * barW + (ns - 1) * 2 + 3}
                    y1={ys(Math.max(0, c.marker))}
                    y2={ys(Math.max(0, c.marker))}
                    stroke="var(--text-primary)"
                    strokeWidth={2}
                    strokeLinecap="round"
                    aria-hidden
                  />
                )}
                {ci % labelEvery === 0 && (
                  <text x={m.left + groupW * ci + groupW / 2} y={m.top + h + 15} textAnchor="middle">
                    {c.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      );
    }
  }
  const t = tip ? { c: model.categories[tip.c], s: model.series[tip.s] } : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {legendH > 0 && (
        <div className="legend">
          {model.series.map((s, i) => (
            <span className="legend-item" key={s.id}>
              <span className="key-rect" style={{ background: seriesColor(s.id, i) }} />
              {s.label}
            </span>
          ))}
          {model.markerLabel && (
            <span className="legend-item">
              <span style={{ width: horizontal ? 2 : 12, height: horizontal ? 12 : 2, background: 'var(--text-primary)' }} />
              {model.markerLabel}
            </span>
          )}
        </div>
      )}
      <div className="chart" ref={ref}>
        {body}
        {tip && t && (
          <div className="tooltip" style={{ left: Math.min(tip.x + 10, width - 180), top: Math.max(0, tip.y - 30) }}>
            <div className="tt-title">{t.c.label}</div>
            {(horizontal ? [t.s] : model.series).map((s, i) => (
              <div className="tt-row" key={s.id}>
                <span className="key-rect" style={{ background: seriesColor(s.id, horizontal ? tip.s : i) }} />
                <b>{fmt(s.values[tip.c], horizontal ? tip.s : i)}</b>
                <span className="name">{s.label}</span>
              </div>
            ))}
            {typeof t.c.marker === 'number' && (
              <div className="tt-row">
                <b>{formatValue(t.c.marker, model.series[0].format, { currency: model.currency })}</b>
                <span className="name">{model.markerLabel}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const barChart: RegistryEntry = {
  component: BarChart,
  isEmpty: (d) => !(d?.rows?.length),
  toTable: (d, w) => {
    const m = toModel(d, w.props ?? {});
    return {
      columns: [
        { key: 'label', label: 'Category' },
        ...m.series.map((s) => ({ key: s.id, label: s.label, format: s.format, currency: m.currency, numeric: true })),
        ...(m.markerLabel ? [{ key: 'marker', label: m.markerLabel, format: m.series[0]?.format, numeric: true }] : []),
      ],
      rows: m.categories.map((c, i) => ({ label: c.label, marker: c.marker, ...Object.fromEntries(m.series.map((s) => [s.id, s.values[i]])) })),
    };
  },
};
